/**
 * Statements: a month at a time, buyers or suppliers.
 *
 * Staff with manage_billing see every party with anything in the month,
 * as a draft until it is finalised, and every statement already issued. A
 * buyer or supplier login sees the statements issued to it.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Badge, Group, Loader, Select, Stack, Table, Text,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  formatPence, monthLabel, previousMonth, useStatements, type StatementKind, type StatementSummary,
} from '../../api/statements';
import { useNavUser } from '../../layout/navUser';
import { hasAction, isScopedUser, seesStatements } from '../../layout/nav';
import { MonoSegmented, PageHeader } from '../../ui';
import { useVocab } from '../../vocab';

/** The last 18 months, newest first, ending last month. */
function months(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  let m = previousMonth();
  for (let i = 0; i < 18; i += 1) {
    out.push({ value: m, label: monthLabel(m) });
    m = previousMonth(m);
  }
  return out;
}

export function statementLink(s: Pick<StatementSummary, 'id' | 'kind' | 'party_id' | 'period'>): string {
  return s.id
    ? `/statements/${encodeURIComponent(s.id)}`
    : `/statements/draft?kind=${s.kind}&party=${encodeURIComponent(s.party_id)}&period=${s.period}`;
}

function StatusBadge({ s }: { s: StatementSummary }) {
  if (s.status === 'paid') return <Badge variant="light" color="teal">Paid</Badge>;
  if (s.status === 'finalised') return <Badge variant="light" color="blue">Issued</Badge>;
  return <Badge variant="light" color="gray">Draft</Badge>;
}

export function StatementsPage() {
  const v = useVocab();
  const user = useNavUser();
  const staff = !isScopedUser(user) && hasAction(user, 'manage_billing');
  const [kind, setKind] = useState<StatementKind>('buyer');
  const [period, setPeriod] = useState<string>(previousMonth());
  const { data, isPending, error, isPlaceholderData } = useStatements(
    staff ? { kind, period } : {},
    seesStatements(user),
  );

  if (!seesStatements(user)) {
    return <Alert color="gray" variant="light">Statements are not available to this login.</Alert>;
  }
  const rows = data?.statements ?? [];
  const totalDue = rows.reduce((s, r) => s + r.totals.total_pence, 0);

  return (
    <Stack gap="lg">
      <PageHeader
        title="Statements"
        description={
          staff
            ? `A calendar month at a time, in UK time. Drafts are worked out live; issue one once the month is over.`
            : 'Statements issued to you. Open one to print it or download every line.'
        }
        actions={
          staff ? (
            <Group gap="sm">
              <MonoSegmented
                value={kind}
                onChange={(k) => setKind(k as StatementKind)}
                data={[
                  { value: 'buyer', label: v.Many('buyer') },
                  { value: 'supplier', label: v.Many('supplier') },
                ]}
                aria-label="Whose statements"
              />
              <Select
                aria-label="Month"
                w={170}
                allowDeselect={false}
                data={months()}
                value={period}
                onChange={(m) => m && setPeriod(m)}
              />
            </Group>
          ) : undefined
        }
      />
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {data && (
        <div className={isPlaceholderData ? 'hf-stale' : 'hf-fresh'}>
          {rows.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              {staff ? `Nothing to bill for ${monthLabel(period)}.` : 'No statements yet.'}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={760}>
              <Table striped verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{staff ? (kind === 'buyer' ? v.One('buyer') : v.One('supplier')) : 'Month'}</Table.Th>
                    <Table.Th>Number</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{kind === 'buyer' || !staff ? 'Gross' : 'Charges'}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{kind === 'buyer' || !staff ? 'Credits' : 'Chargebacks'}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>VAT</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
                    <Table.Th>Due</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((s) => (
                    <Table.Tr key={`${s.kind}-${s.party_id}-${s.period}`}>
                      <Table.Td>
                        <Anchor component={Link} to={statementLink(s)} size="sm" fw={500}>
                          {staff ? s.party_name : monthLabel(s.period)}
                        </Anchor>
                      </Table.Td>
                      <Table.Td><Text size="sm" ff="monospace">{s.number ?? ''}</Text></Table.Td>
                      <Table.Td><StatusBadge s={s} /></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" ff="monospace">{formatPence(s.totals.gross_pence)}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" ff="monospace">{formatPence(-s.totals.deductions_pence)}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" ff="monospace">{formatPence(s.totals.vat_pence)}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" ff="monospace" fw={600}>{formatPence(s.totals.total_pence)}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{s.due_date ?? ''}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {staff && rows.length > 0 && (
            <Text size="sm" c="dimmed" mt="sm" ta="right">
              {`${rows.length} ${rows.length === 1 ? 'statement' : 'statements'}, ${formatPence(totalDue)} in all`}
            </Text>
          )}
        </div>
      )}
    </Stack>
  );
}
